import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, AlertTriangle, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal, SectionHead, EASE } from "@/lib/motion";
import { COMPLEXITY_ITEMS, COMPLEXITY_TIERS, COMPLEXITY_CATEGORIES, FORMS_CHARGED_NOTE, INDIVIDUAL_ONLY_NOTE_POSITIVE, INDIVIDUAL_ONLY_NOTE_NEGATIVE, ITEM_INFO } from "@/data/content";
import { fetchFees } from "@/lib/api";
import { computeFeeTiers } from "@/lib/priceTier";
import StatePicker from "@/components/StatePicker";
import MobileEstimateBar from "@/components/MobileEstimateBar";
import EstimateBreakdown from "@/components/EstimateBreakdown";
import TierMeter from "@/components/TierMeter";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

// Items that render inside the selectable grid (base W-2 and per-state are shown separately)
const SELECTABLE = COMPLEXITY_ITEMS.filter((i) => i.id !== "w2" && i.id !== "multi-state");
const VALID_IDS = SELECTABLE.map((i) => i.id);

export default function Pricing() {
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState(() => {
    const raw = searchParams.get("items");
    if (!raw) return [];
    return raw.split(",").filter((id) => VALID_IDS.includes(id));
  });
  const [states, setStates] = useState([]);
  const [fees, setFees] = useState(null);

  useEffect(() => {
    fetchFees().then(setFees).catch(() => setFees(null));
  }, []);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const feeFor = (id) => (fees?.items || []).find((f) => f.id === id);
  const feeTiers = useMemo(() => (fees?.items ? computeFeeTiers(fees.items) : {}), [fees]);

  const score = useMemo(() => {
    let max = 1;
    for (const id of selected) {
      const c = Number(feeFor(id)?.complexity) || 1;
      if (c > max) max = c;
    }
    return max;
  }, [selected, fees]);
  const tier = COMPLEXITY_TIERS.find((t) => t.level === score) || COMPLEXITY_TIERS[0];

  const estimate = useMemo(() => {
    if (!fees) return null;
    let min = Number(fees.base_min) || 0;
    let max = Number(fees.base_max) || 0;
    for (const id of selected) {
      const item = feeFor(id);
      if (item) {
        min += Number(item.min) || 0;
        max += Number(item.max) || 0;
      }
    }
    if (states.length > 0) {
      const ms = feeFor("multi-state");
      if (ms) {
        min += states.length * (Number(ms.min) || 0);
        max += states.length * (Number(ms.max) || 0);
      }
    }
    return { min, max };
  }, [fees, selected, states]);

  const estimateLink = `/get-started?type=estimate&items=${selected.join(",")}${states.length ? `&states=${states.join(",")}` : ""}`;

  return (
    <div data-testid="pricing-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Pricing"
            title="Transparent fees, estimated live."
            copy="Tax preparation fees depend on the forms and complexity involved. Instead of hiding behind ‘contact us for pricing,’ here is exactly how a fee is built: a base for the return, plus a published range for each additional situation. Select yours and watch the math happen."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-6 border border-steel-border bg-steel-surface p-5" data-testid="state-picker-wrap">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">State</p>
              <p className="mt-1.5 text-xs leading-relaxed text-steel-secondary">
                Which states did you live or earn income in? Each state filed adds the state fee to your
                estimate.
              </p>
              <div className="mt-4">
                <StatePicker selected={states} onChange={setStates} />
              </div>
            </div>
            {fees && (
              <Reveal>
                <div className="mb-4 flex items-center justify-between border border-steel-brass/40 bg-steel-surface p-6" data-testid="pricing-base-fee">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-brass">Every return starts here</p>
                    <h3 className="mt-2 font-display text-xl font-medium text-steel-text">Base return — W-2, simple situations</h3>
                  </div>
                  <p className="font-mono text-xl text-steel-text">
                    ${Number(fees.base_min).toLocaleString()} – ${Number(fees.base_max).toLocaleString()}
                  </p>
                </div>
              </Reveal>
            )}
            <Reveal>
              <div
                className="mb-6 flex items-start gap-3 border-2 border-red-500/60 bg-red-950/30 p-5"
                data-testid="individual-only-note"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                <p className="text-sm leading-relaxed">
                  <span className="text-emerald-400">{INDIVIDUAL_ONLY_NOTE_POSITIVE}</span>{" "}
                  <span className="text-red-400">{INDIVIDUAL_ONLY_NOTE_NEGATIVE}</span>
                </p>
              </div>
            </Reveal>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">
              Add what applies to you
            </p>
            <p className="mb-4 text-xs leading-relaxed text-steel-muted" data-testid="forms-charged-note">
              {FORMS_CHARGED_NOTE}
            </p>
            <div className="space-y-8">
              {COMPLEXITY_CATEGORIES.map((cat, ci) => {
                const catItems = SELECTABLE.filter((i) => i.cat === cat.id);
                if (catItems.length === 0) return null;
                return (
                  <Reveal key={cat.id} delay={ci * 0.05}>
                    <div data-testid={`pricing-category-${cat.id}`}>
                      <div className="mb-3 border-l-2 border-steel-brass/60 pl-3">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-steel-brass">
                          {cat.label}
                        </p>
                        <p className="mt-0.5 text-xs text-steel-muted">{cat.hint}</p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {catItems.map((item) => {
                          const on = selected.includes(item.id);
                          const fee = feeFor(item.id);
                          const info = ITEM_INFO[item.id];
                          const button = (
                            <button
                              data-testid={`pricing-item-${item.id}`}
                              onClick={() => toggle(item.id)}
                              className={`flex items-center gap-3 border p-4 text-left transition-colors duration-300 ${
                                on
                                  ? "border-steel-accent bg-steel-accent/10 shadow-[0_0_18px_rgba(147,51,234,0.25)]"
                                  : "border-steel-border bg-steel-surface hover:border-steel-muted"
                              }`}
                            >
                              <div>
                                <span className={`flex items-center gap-1.5 text-sm ${on ? "font-bold text-steel-brass" : "font-medium text-steel-secondary"}`}>
                                  {item.label}
                                  {info && <Info className="h-3 w-3 shrink-0 text-steel-muted" />}
                                </span>
                                {fee && (
                                  <span className="mt-1.5 flex flex-col gap-1">
                                    {Number(fee.max) > 0 && (
                                      <span className="font-mono text-[11px] text-steel-muted">
                                        +${Number(fee.min).toLocaleString()} – ${Number(fee.max).toLocaleString()}
                                      </span>
                                    )}
                                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                      <TierMeter kind="price" value={feeTiers[item.id]} testid={`pricing-item-tier-${item.id}`} />
                                      <TierMeter kind="complexity" value={fee.complexity} testid={`pricing-item-cmplx-${item.id}`} />
                                    </span>
                                  </span>
                                )}
                              </div>
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center border font-mono text-[10px] transition-colors duration-300 ${
                                  on ? "border-steel-accent text-steel-accent" : "border-steel-border text-steel-muted"
                                }`}
                              >
                                {on ? "+" : ""}
                              </span>
                            </button>
                          );
                          if (!info) return <div key={item.id}>{button}</div>;
                          return (
                            <HoverCard key={item.id} openDelay={150} closeDelay={80}>
                              <HoverCardTrigger asChild>{button}</HoverCardTrigger>
                              <HoverCardContent
                                data-testid={`pricing-item-info-${item.id}`}
                                className="w-80 border-steel-border bg-steel-elevated text-steel-text"
                              >
                                <p className="font-display text-sm font-medium text-steel-text">{item.label}</p>
                                <div className="mt-3 space-y-3">
                                  <div>
                                    <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                                      <CheckCircle2 className="h-3 w-3" /> Who qualifies
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-steel-secondary">{info.eligibility}</p>
                                  </div>
                                  <div>
                                    <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">
                                      <AlertCircle className="h-3 w-3" /> Limitations
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-steel-secondary">{info.limitations}</p>
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          );
                        })}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selected.length || states.length ? tier.name : "empty"}
                  data-testid="pricing-result"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="glass border border-steel-border p-10"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">Your estimate</p>
                  {selected.length === 0 && states.length === 0 ? (
                    <>
                      <h3 className="mt-5 font-display text-2xl font-medium text-steel-text">
                        Base return{fees ? `: $${Number(fees.base_min).toLocaleString()} – $${Number(fees.base_max).toLocaleString()}` : ""}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-steel-secondary">
                        A straightforward W-2 return sits at the base fee. Add situations on the left and the
                        range updates live.
                      </p>
                      <div className="mt-6 border border-steel-brass/40 bg-steel-elevated p-5" data-testid="no-other-forms-box">
                        <p className="text-sm font-medium text-steel-text">
                          Nothing else to add? If you have no other forms, deductions or credits to claim,
                          you're all set.
                        </p>
                        <Link
                          to={estimateLink}
                          data-testid="no-other-forms-start-btn"
                          className="group mt-4 flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
                        >
                          Start My Tax Return
                          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <p className="mt-4 text-xs leading-relaxed text-steel-muted">
                          Not sure? Browse the categories to the left for anything that might apply, or reach
                          out and we'll help you figure out what you may qualify for:{" "}
                          <a href="mailto:jared@steeleindustriesnh.com" className="text-steel-brass underline">
                            jared@steeleindustriesnh.com
                          </a>
                          .
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="mt-5 font-display text-3xl font-medium text-steel-text">{tier.name}</h3>
                      {estimate && (
                        <p className="neon-text mt-3 font-display text-4xl font-medium text-steel-brass" data-testid="pricing-total">
                          ${estimate.min.toLocaleString()} – ${estimate.max.toLocaleString()}
                        </p>
                      )}
                      <EstimateBreakdown fees={fees} selected={selected} states={states} />
                      <p className="mt-4 text-sm leading-relaxed text-steel-secondary">{tier.desc}</p>
                      <p className="mt-3 text-xs leading-relaxed text-steel-muted">
                        Indicative range, not a quote — your exact fee is confirmed in writing before any work
                        begins.
                      </p>
                      <Link
                        to={estimateLink}
                        data-testid="pricing-estimate-btn"
                        className="group mt-8 flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold text-white shadow-[0_0_26px_rgba(168,85,247,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover hover:shadow-[0_0_46px_rgba(168,85,247,0.55)]"
                      >
                        Get a Preparation Estimate
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
              <Reveal delay={0.1}>
                <p className="mt-6 text-xs leading-relaxed text-steel-muted">
                  Fees are set per engagement and published here for transparency. Unusually complex matters
                  may be quoted individually — you will always know the number before we start.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
        <MobileEstimateBar show={selected.length > 0 || states.length > 0} estimate={estimate} link={estimateLink} />
      </section>
    </div>
  );
}
